import { Controller } from "@hotwired/stimulus"

const DAY_MS = 24 * 60 * 60 * 1000
// Retain a dismissal through Turbo navigation if device storage is unavailable.
const memory = new Map()

export default class extends Controller {
  static values = { campaign: String, appVersion: String }

  connect() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.trackImpression()
    })
    this.observer.observe(this.element)
    this.refresh()
  }

  disconnect() {
    this.observer.disconnect()
  }

  refresh() {
    this.element.hidden = document.hidden ||
      document.documentElement.hasAttribute("data-turbo-preview") ||
      this.recentlyRecorded("dismissed-at", 7 * DAY_MS)
    if (!this.element.hidden) {
      // Recheck visibility when returning to a tab, including after snooze expiry.
      this.observer.unobserve(this.element)
      this.observer.observe(this.element)
    }
  }

  hide() {
    this.element.hidden = true
  }

  dismiss() {
    this.record("dismissed-at")
    this.hide()
    this.track("app_update_prompt_dismissed")
  }

  trackClick() {
    this.track("app_update_prompt_clicked")
  }

  trackImpression() {
    if (this.element.hidden || document.hidden || this.recentlyRecorded("shown-at", DAY_MS)) return

    // One visible impression per device/campaign/day, including Turbo restores.
    this.record("shown-at")
    this.track("app_update_prompt_shown")
  }

  recentlyRecorded(name, duration) {
    const key = this.storageKey(name)
    let timestamp = Number(memory.get(key)) || 0
    try {
      timestamp = Math.max(timestamp, Number(window.localStorage.getItem(key)) || 0)
    } catch (_error) {
      // The in-memory value still applies for this WebView's lifetime.
    }
    const age = Date.now() - timestamp
    return timestamp > 0 && age >= 0 && age < duration
  }

  record(name) {
    const key = this.storageKey(name)
    const timestamp = String(Date.now())
    memory.set(key, timestamp)
    try {
      window.localStorage.setItem(key, timestamp)
    } catch (_error) {
      // The prompt remains usable when persistent storage is unavailable.
    }
  }

  storageKey(name) {
    return `skinsort:app-update:${this.campaignValue}:${name}`
  }

  track(name) {
    try {
      const payload = new Blob([JSON.stringify({
        analytic: {
          name,
          app_version: this.appVersionValue,
          properties: { campaign: this.campaignValue }
        }
      })], { type: "application/json" })
      navigator.sendBeacon("/api/analytics/track", payload)
    } catch (_error) {
      // Analytics must not prevent dismissal or the App Store handoff.
    }
  }
}
